import {
  CircleFragment,
  CircleLinkFragment,
  CircleMemberFragment,
  Governance_Mode_Enum,
  MemberFragment,
  Member_Role_Enum,
  RoleSummaryFragment,
  ThreadFragment,
} from '../gql'
import {
  EntitiesChanges,
  EntityChangeType,
  LogDisplay,
  LogType,
} from '../model/log'
import { Participant } from '../model/member'
import { isRoleWorkField } from '../model/role'
import { EntityWithScope, ParticipantsScope } from '../model/participants'

// A circle member joined with its member entity.
export interface CircleMemberJoined {
  id: string
  focus?: string | null
  member: MemberFragment
}

// The direct children of a circle, split by their nature. Invited circles are
// not here: they are links, not children (see invitedCirclesOf).
export interface CircleChildrenGroups {
  // Link roles (Leader, Représentant…), base or made by hand: they tie the
  // circle to the one above, so they open the list (#119, Yohan 25/09/2026).
  links: readonly CircleFragment[]
  // Other roles built on a base role, reusable across the org chart.
  structural: readonly CircleFragment[]
  // Non-base roles that hold children of their own.
  subCircles: readonly CircleFragment[]
  // Non-base leaf roles.
  functional: readonly CircleFragment[]
}

// What a member is allowed to edit on a circle (before the read-only `editable`
// gate applied by the caller). Mirrored by the Hasura permissions on the
// circle, role, circle_link and circle_member tables.
export interface CirclePermissions {
  canEditCircle: boolean
  canEditRole: boolean
  // The role's work fields (ROLE_WORK_FIELDS): looser than canEditRole, since
  // the holder keeps them in every governance mode.
  canEditRoleWorkFields: boolean
  canEditMembers: boolean
  canEditSubCircles: boolean
  canEditSubCirclesParentLinks: boolean
}

const noCirclePermissions: CirclePermissions = {
  canEditCircle: false,
  canEditRole: false,
  canEditRoleWorkFields: false,
  canEditMembers: false,
  canEditSubCircles: false,
  canEditSubCirclesParentLinks: false,
}

// The current member acting as a leader of the circles a proposal may change
// (proposal editor): they are added to those circles' leaders, which propagates
// to participants and to the owner checks of their sub-circles.
//
// Several circles, not one: a tension carried by a link may address either of
// the two circles it joins, and the choice is only settled once the author has
// modified something. A single id could not express that, and it was exactly
// that rigidity which left a tension unable to change the circle deciding it
// (Yocoop, 09/08/2026).
export interface ActingLeader {
  circleIds: readonly string[]
  member: MemberFragment
}

const byName = (a: { name: string }, b: { name: string }) =>
  a.name.localeCompare(b.name)

// Inputs to build an OrgData. The first five fields match the OrgData GraphQL
// fragment, so a query result can be passed almost as-is.
export interface OrgDataInput {
  circles: CircleFragment[]
  circleMembers: CircleMemberFragment[]
  circleLinks: CircleLinkFragment[]
  roles: RoleSummaryFragment[]
  members: MemberFragment[]
  // The org's governance mode, used by getCirclePermissions.
  governanceMode: Governance_Mode_Enum
  // The current member acting as a leader of a circle (proposal editor).
  actingLeader?: ActingLeader
  // When true, archived circles/roles are indexed too (so an archived subtree
  // can be walked, e.g. to restore it). Members and links stay active-only so
  // leadership and permission checks remain correct.
  includeArchived?: boolean
}

// Indexed view of an org's structure: the single org data object, holding the
// active entities and the lookups + operations over them. Nothing materializes
// a nested CircleFull tree anymore.
//
// Instances are immutable after construction (callers always create a fresh
// OrgData rather than mutating one), so pure traversals are memoized on demand.
// All accessors return readonly views: never mutate them, doing so would corrupt
// the indexes and caches.
export class OrgData {
  // Active entities (archived filtered out), members sorted by name
  readonly circles: readonly CircleFragment[]
  readonly members: readonly MemberFragment[]
  readonly roles: readonly RoleSummaryFragment[]

  // The org's governance mode, used by getCirclePermissions.
  readonly governanceMode: Governance_Mode_Enum
  // The current member acting as a leader of a circle (proposal editor).
  private readonly actingLeader?: ActingLeader

  // By-id lookups (also index archived circles/roles, so an archived
  // circle/role can still be resolved when its panel is opened)
  readonly circleById: ReadonlyMap<string, CircleFragment>
  readonly roleById: ReadonlyMap<string, RoleSummaryFragment>
  readonly memberById: ReadonlyMap<string, MemberFragment>

  // Forward and inverse relation indexes
  private readonly membersByCircle = new Map<string, CircleMemberJoined[]>()
  private readonly linksByCircle = new Map<string, CircleLinkFragment[]>()
  private readonly childrenByCircle = new Map<string, CircleFragment[]>()
  private readonly circlesByMember = new Map<string, CircleFragment[]>()

  // On-demand caches for pure derivations
  private readonly hueCache = new Map<string, number | null>()
  private readonly leadersCache = new Map<string, readonly Participant[]>()
  private readonly participantsCache = new Map<string, readonly Participant[]>()
  private readonly deepParticipantsCache = new Map<
    string,
    readonly Participant[]
  >()
  private readonly participantCirclesCache = new Map<
    string,
    readonly CircleFragment[]
  >()
  private activeMembersList?: readonly MemberFragment[]
  private orgOwnersList?: readonly MemberFragment[]

  constructor(input: OrgDataInput) {
    const {
      circles,
      circleMembers,
      circleLinks,
      roles,
      members,
      governanceMode,
      actingLeader,
      includeArchived = false,
    } = input
    this.governanceMode = governanceMode
    this.actingLeader = actingLeader

    // Exclude archived circles and members: the subscription returns only
    // active ones, but the proposal draft and the demo mark them archived in
    // place (in-memory "delete").
    const indexedCircles = includeArchived
      ? circles
      : circles.filter((c) => c.archivedAt == null)
    this.circles = indexedCircles
    this.members = members.filter((m) => m.archivedAt == null).sort(byName)
    this.roles = roles
    this.circleById = new Map(indexedCircles.map((c) => [c.id, c]))
    this.roleById = new Map(roles.map((r) => [r.id, r]))
    this.memberById = new Map(this.members.map((m) => [m.id, m]))

    for (const cm of circleMembers) {
      if (cm.archivedAt != null) continue
      const circle = this.circleById.get(cm.circleId)
      if (!circle) continue
      const member = this.memberById.get(cm.memberId)
      if (!member) continue

      // Forward: members of a circle
      const list = this.membersByCircle.get(cm.circleId)
      const entry = { id: cm.id, focus: cm.focus ?? null, member }
      if (list) list.push(entry)
      else this.membersByCircle.set(cm.circleId, [entry])

      // Inverse: circles a member is a direct member of
      const circleList = this.circlesByMember.get(cm.memberId)
      if (circleList) circleList.push(circle)
      else this.circlesByMember.set(cm.memberId, [circle])
    }

    for (const cl of circleLinks) {
      if (cl.archivedAt != null || !this.circleById.has(cl.parentId)) continue
      const list = this.linksByCircle.get(cl.parentId)
      if (list) list.push(cl)
      else this.linksByCircle.set(cl.parentId, [cl])
    }

    for (const c of indexedCircles) {
      if (!c.parentId) continue
      const list = this.childrenByCircle.get(c.parentId)
      if (list) list.push(c)
      else this.childrenByCircle.set(c.parentId, [c])
    }
  }

  // ---- Internal helpers ----

  // Direct parent of a circle, if any.
  private parentOf(circle: CircleFragment): CircleFragment | undefined {
    return circle.parentId ? this.circleById.get(circle.parentId) : undefined
  }

  // Whether a circle represents its parent (a "representant" / parent link).
  private isParentLink(circle: CircleFragment): boolean {
    return !!this.roleById.get(circle.roleId)?.parentLink
  }

  // Members representing a circle through its parent-link sub-circles.
  private representativesOf(circleId: string): Participant[] {
    return this.childrenOf(circleId)
      .filter((c) => this.isParentLink(c))
      .flatMap((c) =>
        this.membersOf(c.id).map(
          ({ member }): Participant => ({ circleId: c.id, member })
        )
      )
  }

  // ---- By-id lookups ----

  getCircle(id?: string): CircleFragment | undefined {
    return id ? this.circleById.get(id) : undefined
  }
  getRole(id?: string): RoleSummaryFragment | undefined {
    return id ? this.roleById.get(id) : undefined
  }
  getMember(id?: string): MemberFragment | undefined {
    return id ? this.memberById.get(id) : undefined
  }
  // The circle carrying a role. A base role is shared by many circles, so the
  // answer is the first one found and means little — callers that care about
  // base roles must say which circle they mean.
  circleOfRole(roleId: string): CircleFragment | undefined {
    return this.circles.find((c) => c.roleId === roleId)
  }
  // Members linked to a user account (those who have actually joined).
  getActiveMembers(): readonly MemberFragment[] {
    return (this.activeMembersList ??= this.members.filter((m) => !!m.userId))
  }
  // Owners of the organization, who may act on any circle in any governance
  // mode. Every "who can do this" list ends with them.
  getOrgOwners(): readonly MemberFragment[] {
    return (this.orgOwnersList ??= this.members.filter(
      (m) => m.role === Member_Role_Enum.Owner
    ))
  }

  // ---- Relations ----

  membersOf(circleId: string): readonly CircleMemberJoined[] {
    return this.membersByCircle.get(circleId) ?? []
  }
  // Circles a member is a direct member of (inverse of membersOf).
  circlesOfMember(memberId: string): readonly CircleFragment[] {
    return this.circlesByMember.get(memberId) ?? []
  }
  linksOf(circleId: string): readonly CircleLinkFragment[] {
    return this.linksByCircle.get(circleId) ?? []
  }
  // Circles that invited a circle through a link (the link host circles).
  invitingCirclesOf(circleId: string): readonly CircleFragment[] {
    return this.circles.filter((c) =>
      this.linksOf(c.id).some((link) => link.circleId === circleId)
    )
  }
  // Circles invited into a circle through its links.
  invitedCirclesOf(circleId: string): readonly CircleFragment[] {
    return this.linksOf(circleId)
      .map((link) => this.circleById.get(link.circleId))
      .filter((c): c is CircleFragment => c !== undefined)
  }
  // Direct children of a circle.
  childrenOf(circleId: string): readonly CircleFragment[] {
    return this.childrenByCircle.get(circleId) ?? []
  }
  // A circle's SCOPE: itself, then its direct children — never deeper.
  //
  // This is the perimeter every screen means when it offers "the roles of this
  // circle": the meeting's tension picker (REUN-02, e834b3cc), the next step's
  // role selector (G2-D18), the meeting board, the quick tension form. It was
  // copied by hand in each of them, and its justification with it — three
  // separate comments repeated "childrenOf, never descendantsOf". One rule, one
  // place, one explanation:
  //
  // 🔑 Direct children only, because `circle_participant` lifts a lead one level
  // — from a direct child up to its parent, and no further. Going deeper would
  // offer roles whose holders are not participants of this circle, which is
  // exactly the confusion the permission model avoids.
  //
  // An unknown circle yields an empty list rather than throwing: a missing
  // piece of data must never deprive anyone of their tools.
  circleWithChildren(circleId: string): readonly CircleFragment[] {
    const circle = this.circleById.get(circleId)
    if (!circle) return []
    return [circle, ...this.childrenOf(circleId)]
  }
  // Whether a node is a circle (has at least one structural child: a sub-circle
  // or a leaf role) rather than a leaf role. People-members are not children
  // (they live in membersByCircle), so a leaf role held by members is not a
  // circle. Mirrors the org chart's hasSubCircles (packages/graph nodeKind).
  isCircle(circleId: string): boolean {
    return this.childrenOf(circleId).length > 0
  }
  // Children of a circle, split into the groups the circle panel lists them in.
  childrenGroupsOf(circleId: string): CircleChildrenGroups {
    const links: CircleFragment[] = []
    const structural: CircleFragment[] = []
    const subCircles: CircleFragment[] = []
    const functional: CircleFragment[] = []

    for (const child of this.childrenOf(circleId)) {
      const role = this.getRole(child.roleId)
      // A link role first, whatever else it is (#119). Then `base` before the
      // children check on purpose: a base role that holds children stays
      // structural instead of sliding into subCircles.
      if (role?.parentLink) {
        links.push(child)
      } else if (role?.base) {
        structural.push(child)
      } else if (this.isCircle(child.id)) {
        subCircles.push(child)
      } else {
        functional.push(child)
      }
    }

    const byRoleName = (a: CircleFragment, b: CircleFragment) =>
      (this.getRole(a.roleId)?.name ?? '').localeCompare(
        this.getRole(b.roleId)?.name ?? ''
      )

    return {
      links: links.sort(byRoleName),
      structural: structural.sort(byRoleName),
      subCircles: subCircles.sort(byRoleName),
      functional: functional.sort(byRoleName),
    }
  }
  // All descendants of a circle (children, recursively).
  descendantsOf(circleId: string): readonly CircleFragment[] {
    return this.childrenOf(circleId).flatMap((c) => [
      c,
      ...this.descendantsOf(c.id),
    ])
  }
  // Ancestors of a circle, from the root down to its direct parent.
  parentsOf(circle: CircleFragment): readonly CircleFragment[] {
    const parents: CircleFragment[] = []
    let parent = this.parentOf(circle)
    while (parent) {
      parents.unshift(parent)
      parent = this.parentOf(parent)
    }
    return parents
  }
  // Ancestors of a circle plus the circle itself (root first).
  andParentsOf(circleId: string): readonly CircleFragment[] {
    const circle = this.circleById.get(circleId)
    if (!circle) return []
    return [...this.parentsOf(circle), circle]
  }

  // ---- Color ----

  // Resolve a circle's color hue on demand: its role's colorHue, or the nearest
  // parent whose role defines one. Cached per circle.
  getColor(circleId: string): number | null {
    const cached = this.hueCache.get(circleId)
    if (cached !== undefined) return cached

    let current: CircleFragment | undefined = this.circleById.get(circleId)
    let hue: number | null = null
    while (current) {
      const role = this.roleById.get(current.roleId)
      if (role && typeof role.colorHue === 'number') {
        hue = role.colorHue
        break
      }
      current = this.parentOf(current)
    }

    this.hueCache.set(circleId, hue)
    return hue
  }

  // ---- Participants ----

  getLeaders(circleId: string): readonly Participant[] {
    if (!this.circleById.has(circleId)) return []

    const cached = this.leadersCache.get(circleId)
    if (cached) return cached

    // Sub-circle leaders (parent-link circles)
    const leaders = this.representativesOf(circleId)

    // If no representant, take direct members
    let result =
      leaders.length !== 0
        ? leaders
        : this.membersOf(circleId).map(
            ({ member }): Participant => ({ circleId, member })
          )

    // Acting leader (proposal editor): current member acts as a leader here
    const acting = this.actingLeader
    if (
      acting &&
      acting.circleIds.includes(circleId) &&
      !result.some((p) => p.member.id === acting.member.id)
    ) {
      result = [...result, { circleId, member: acting.member }]
    }

    this.leadersCache.set(circleId, result)
    return result
  }

  getParticipants(
    circleId: string,
    includeChildren = false
  ): readonly Participant[] {
    if (includeChildren) {
      const cached = this.deepParticipantsCache.get(circleId)
      if (cached) return cached

      const participants = this.getParticipants(circleId)
      const subCirclesParticipants = this.childrenOf(circleId)
        // Skip leaders (already in participants)
        .filter((c) => !this.isParentLink(c))
        .flatMap((c) => this.getParticipants(c.id, true))
      const result = [...subCirclesParticipants, ...participants]

      this.deepParticipantsCache.set(circleId, result)
      return result
    }

    if (!this.circleById.has(circleId)) return []

    const cached = this.participantsCache.get(circleId)
    if (cached) return cached

    let hasLeader = false

    // Leaders of roles and sub-circles
    const leaders = this.childrenOf(circleId).flatMap((c) => {
      if (this.isParentLink(c)) {
        const cMembers = this.membersOf(c.id)
        if (cMembers.length > 0) hasLeader = true
        return cMembers.map(
          ({ member }): Participant => ({
            circleId: c.id,
            member,
            leader: true,
          })
        )
      } else {
        return this.getLeaders(c.id).map((p) => ({ ...p, circleId: c.id }))
      }
    })

    // Direct members
    const directParticipants = this.membersOf(circleId).map(
      ({ member }): Participant => ({ circleId, member, leader: !hasLeader })
    )

    // Links to other circles
    const links = this.linksOf(circleId)
      .flatMap((link) =>
        this.getLeaders(link.circleId).map((p) => ({
          ...p,
          circleId: link.circleId,
        }))
      )
      .map((p) => ({ ...p, invited: true }))

    const result = [...leaders, ...directParticipants, ...links]
    this.participantsCache.set(circleId, result)
    return result
  }

  // Circles where a member participates (directly or as a representant)
  getParticipantCircles(memberId: string): readonly CircleFragment[] {
    const cached = this.participantCirclesCache.get(memberId)
    if (cached) return cached

    const directMemberCircles = this.circlesByMember.get(memberId) ?? []

    const leaderCircles: CircleFragment[] = []

    const participantCircles = directMemberCircles.reduce<CircleFragment[]>(
      (acc, circle) => {
        const parent = this.parentOf(circle)
        if (!parent) return acc

        const hasLeader = this.childrenOf(circle.id).some(
          (child) =>
            this.isParentLink(child) && this.membersOf(child.id).length > 0
        )
        if (hasLeader) return acc

        acc.push(parent)

        if (this.isParentLink(circle)) {
          const grandParent = this.parentOf(parent)
          if (grandParent) acc.push(grandParent)
          leaderCircles.push(parent)
        }
        return acc
      },
      []
    )

    const invitedCircles = this.circles.filter((circle) =>
      this.linksOf(circle.id).some((link) =>
        leaderCircles.some((c) => link.circleId === c.id)
      )
    )

    const result = [
      ...directMemberCircles,
      ...participantCircles,
      ...invitedCircles,
    ]
    this.participantCirclesCache.set(memberId, result)
    return result
  }

  // ---- Permissions ----

  // Whether a circle is led through parent-link sub-circles (representatives),
  // as opposed to its own direct members.
  hasRepresentatives(circleId: string): boolean {
    return this.representativesOf(circleId).length > 0
  }

  // Whether a member leads a circle (one of its leaders: representatives if any,
  // otherwise its direct members; plus the acting leader in a proposal draft).
  isCircleLeader(circleId: string, memberId?: string): boolean {
    if (!memberId) return false
    if (this.getLeaders(circleId).some((p) => p.member.id === memberId)) {
      return true
    }
    return (
      this.hasOwnersStandingIn(circleId) &&
      this.getOrgOwners().some((m) => m.id === memberId)
    )
  }

  // A root circle with neither link nor direct member has no one to lead it:
  // the org owners stand in, and only there (#121, Q13). Mirrored by the SQL
  // view circle_leader. Kept out of getLeaders, which also feeds displays
  // (leaders lists, role review) where owners would read as real leads.
  hasOwnersStandingIn(circleId: string | undefined): boolean {
    const circle = this.getCircle(circleId)
    return !!circle && !circle.parentId && this.getLeaders(circle.id).length === 0
  }

  // The circle whose leaders "own" a circle: its grandparent if the circle's
  // role is a parent link (representative), otherwise its direct parent.
  ownerCircleOf(circle: CircleFragment): CircleFragment | undefined {
    const role = this.roleById.get(circle.roleId)
    const parent = this.getCircle(circle.parentId || undefined)
    if (role?.parentLink && parent?.parentId) {
      return this.getCircle(parent.parentId)
    }
    return parent
  }

  // The nearest circle that is, or contains, both circles: the one deciding a
  // move from `aId` to `bId` (#201). Undefined when either is unknown.
  commonOwnerOf(
    aId: string | undefined | null,
    bId: string | undefined | null
  ): CircleFragment | undefined {
    const above = new Set<string>()
    let c = this.getCircle(bId || undefined)
    for (; c; c = this.getCircle(c.parentId || undefined)) above.add(c.id)
    c = this.getCircle(aId || undefined)
    for (; c; c = this.getCircle(c.parentId || undefined)) {
      if (above.has(c.id)) return c
    }
    return undefined
  }

  // Whether a member owns a circle (leads its owner circle).
  // The root circle has no circle above it: its own leads own it (#121), so
  // they can assign its direct members now that org owners hold no bypass.
  isCircleOwner(circle: CircleFragment, memberId?: string): boolean {
    const ownerCircle = circle.parentId ? this.ownerCircleOf(circle) : circle
    return !!ownerCircle && this.isCircleLeader(ownerCircle.id, memberId)
  }

  // What a member may edit on a circle, given the org governance mode and the
  // member's org-level standing. Single source of truth shared with the Hasura
  // permissions; the caller still gates the result with an `editable` flag for
  // read-only contexts (preview, share, in-memory draft). Takes the circle and
  // role explicitly so an archived circle (resolved outside the active index)
  // can still be evaluated.
  //
  // - Org owners hold no bypass on the chart (#121, 24/09/2026): they follow
  //   their roles like everyone else. Two exceptions: base roles, a shared
  //   org-level definition, stay theirs outside Strict; and they stand in as
  //   leads of a root circle nobody leads (isCircleLeader).
  // - Free: every member edits the whole chart.
  // - Agile: the circle's owner/leader edits it directly, and its holder edits
  //   the role itself (GOUV-15).
  // - Strict: structural edits go through proposals. Three things stay open:
  //   member assignment, to the circle's lead (representatives, else the owner);
  //   the creation of parent-link roles, to the owner — a circle must have a
  //   lead, so that link is compliance, not an evolution of the structure
  //   (Yocoop, 16/07); and the role's work fields, to its holder (GOUV-15).
  getCirclePermissions(
    circle: CircleFragment,
    role: RoleSummaryFragment,
    memberId: string | undefined,
    isOrgMember: boolean,
    isOrgOwner: boolean
  ): CirclePermissions {
    if (!isOrgMember) return noCirclePermissions

    const isFree = this.governanceMode === Governance_Mode_Enum.Free
    const isStrict = this.governanceMode === Governance_Mode_Enum.Strict
    const isLeader = this.isCircleLeader(circle.id, memberId)
    const isOwner = this.isCircleOwner(circle, memberId)
    // Sub-circles can only be added under a real circle (not a single-member or
    // parent-link role).
    const subCircles = role.singleMember === false && role.parentLink === false

    // Structural edits: the relevant lead, and never directly under strict
    // governance (changes go through proposals).
    //
    // The org owner is bound by strict governance too (D6, 05/08/2026). Strict
    // is an explicit choice by the org to route every change through a
    // proposal; a silent exception for one person voids that choice. The escape
    // hatch remains, but becomes visible: an owner who must act can still change
    // the governance mode — a setting, not a structural edit — so unblocking an
    // org means leaving strict in the open, fixing, and going back.
    //
    // Free and Agile no longer keep an owner bypass either (#121, Q8): an owner
    // acts through their roles. Someone stays able to act, since owners stand
    // in as leads of a root circle nobody leads (isCircleLeader), and an owner
    // may still switch the mode, in the open, to unblock the org.
    const canEditCircle = !isStrict && (isFree || isOwner)

    // The role's holder: whoever leads the circle carrying it — its
    // representatives when it has any, its direct members otherwise. That is
    // isCircleLeader, the definition canEditMembers already relies on, so the
    // word "holder" keeps one meaning across the model.
    //
    // Base roles are excluded: Leader, Facilitator and the like are a single
    // shared record reused by every circle, so one holder's checklist would
    // become everyone's, overwritten by the next. Making them personal per
    // circle is a separate feature (GOUV-15, 08/08/2026).
    const isHolder = !role.base && isLeader

    // Agile reopens the whole role to its holder: describing what a role does
    // is knowledge its holder owns. The structure around it (archiving, moving,
    // duplicating) stays with the circle hosting it, hence canEditCircle is
    // untouched. Free already grants everything, Strict grants nothing here.
    const canEditRole = role.base
      ? isOrgOwner && !isStrict
      : canEditCircle || (isHolder && !isStrict)

    // Work fields follow the role's holder, in every mode including Strict.
    const canEditRoleWorkFields = canEditRole || isHolder
    const canEditSubCircles = subCircles && !isStrict && (isFree || isLeader)
    // Parent links are the exception, in strict mode too: attaching a circle to
    // its parent is compliance, not an evolution of the structure — holacracy
    // requires a circle to have a lead, so forbidding the creation of that link
    // would forbid the very thing the mode demands. The holder is unchanged
    // (the owner, i.e. the parent circle's lead), only the strict exclusion is
    // lifted. Creating a regular role still goes through a proposal.
    const canEditSubCirclesParentLinks = subCircles && (isFree || isOwner)

    // Member assignment is operational: the circle's lead keeps it in every
    // mode (representatives if any, otherwise the owner).
    const canEditMembers =
      isFree || (this.hasRepresentatives(circle.id) ? isLeader : isOwner)

    return {
      canEditCircle,
      canEditRole,
      canEditRoleWorkFields,
      canEditMembers,
      canEditSubCircles,
      canEditSubCirclesParentLinks,
    }
  }

  // Whether a member may move a circle under a target (null: beside the root).
  //
  // The direct rule: editing the moved circle and adding a sub-circle to the
  // target. In a proposal (#201, 20/10/2026), the circle containing both the
  // start and the end may also do it: the circle above takes a role back, or
  // moves it between two of its circles, without leading the circle giving it
  // up. The draft's one-circle check then sends the vote to that same circle.
  canMoveCircle(
    circle: CircleFragment,
    targetCircleId: string | null,
    memberId: string | undefined,
    isOrgMember: boolean,
    isOrgOwner: boolean
  ): boolean {
    if (!isOrgMember) return false
    if (!targetCircleId) {
      return this.governanceMode === Governance_Mode_Enum.Free
    }
    const role = this.getRole(circle.roleId)
    const target = this.getCircle(targetCircleId)
    const targetRole = target && this.getRole(target.roleId)
    if (!role || !target || !targetRole) return false
    if (
      target.id === circle.id ||
      this.descendantsOf(circle.id).some((c) => c.id === target.id)
    ) {
      return false
    }

    const perms = (c: CircleFragment, r: RoleSummaryFragment) =>
      this.getCirclePermissions(c, r, memberId, isOrgMember, isOrgOwner)
    const onTarget = perms(target, targetRole)
    const canAddUnderTarget = role.parentLink
      ? onTarget.canEditSubCirclesParentLinks
      : onTarget.canEditSubCircles
    if (perms(circle, role).canEditCircle && canAddUnderTarget) return true

    const acting = this.actingLeader
    if (!acting || !memberId || acting.member.id !== memberId) return false
    if (targetRole.singleMember || targetRole.parentLink) return false
    const decider = this.commonOwnerOf(circle.parentId, target.id)
    return !!decider && acting.circleIds.includes(decider.id)
  }

  // Permissions of a circle resolved by id, or undefined when the circle is
  // not indexed (archived circles are resolved outside the active index).
  private getCirclePermissionsById(
    circleId: string,
    memberId: string | undefined,
    isOrgMember: boolean,
    isOrgOwner: boolean
  ): CirclePermissions | undefined {
    const circle = this.getCircle(circleId)
    const role = circle && this.getRole(circle.roleId)
    if (!circle || !role) return undefined
    return this.getCirclePermissions(
      circle,
      role,
      memberId,
      isOrgMember,
      isOrgOwner
    )
  }

  // Whether a member may undo a logged org-chart change. Undoing replays the
  // inverse mutations with the member's own rights, so it needs the same
  // permission as the original edit (the Hasura permissions reject it
  // otherwise). Circle archives and creations are undone through the backend
  // routes, which run this same check on data including archived circles: when
  // the target is no longer indexed here, leave the decision to them.
  canCancelLog(
    display: LogDisplay,
    memberId: string | undefined,
    isOrgMember: boolean,
    isOrgOwner: boolean,
    // The log's changes: an update of the work fields only (GOUV-15) needs the
    // looser right the holder has, not the right on the role's definition
    changes?: EntitiesChanges
  ): boolean {
    if (!isOrgMember) return false
    // Repointing a circle to another role (separating it from a base role) and
    // turning a role into a base role are written by the server only: the app
    // cannot undo them (#94)
    if (
      changes?.circles?.some(
        (change) =>
          change.type === EntityChangeType.Update &&
          'roleId' in (change.newData ?? {})
      ) ||
      changes?.roles?.some(
        (change) =>
          change.type === EntityChangeType.Update &&
          'base' in (change.newData ?? {})
      )
    ) {
      return false
    }
    const permsOf = (circleId: string) =>
      this.getCirclePermissionsById(circleId, memberId, isOrgMember, isOrgOwner)

    switch (display.type) {
      case LogType.CircleMemberAdd:
      case LogType.CircleMemberRemove:
        return permsOf(display.id)?.canEditMembers ?? true

      case LogType.CircleLinkAdd:
      case LogType.CircleLinkRemove:
        // display.id is the host circle, which owns the link.
        return permsOf(display.id)?.canEditSubCircles ?? true

      case LogType.RoleCreate:
      case LogType.RoleUpdate:
      case LogType.RoleArchive: {
        // display.id is a role id. A base role is a shared definition, reserved
        // to org owners outside Strict; a regular role belongs to a single circle.
        const role = this.getRole(display.id)
        if (role?.base) {
          return (
            isOrgOwner && this.governanceMode !== Governance_Mode_Enum.Strict
          )
        }
        const circle = this.circles.find((c) => c.roleId === display.id)
        if (!circle) return true
        const perms = permsOf(circle.id)
        if (perms?.canEditRole) return true
        // GOUV-15: a role's checklist, indicators and notes follow its holder
        // in every mode, and role.update lets them undo such a change
        const roleChanges = changes?.roles ?? []
        const workFieldsOnly =
          display.type === LogType.RoleUpdate &&
          roleChanges.length > 0 &&
          roleChanges.every(
            (change) =>
              change.type === EntityChangeType.Update &&
              Object.keys(change.newData ?? {}).every(isRoleWorkField)
          )
        return workFieldsOnly && !!perms?.canEditRoleWorkFields
      }

      default:
        // CircleCreate, CircleCopy, CircleMove, CircleArchive
        return permsOf(display.id)?.canEditCircle ?? true
    }
  }

  // Whether a member may edit at least one circle of the chart. Gates the
  // chart-wide edit affordances (drag & drop), which would otherwise be offered
  // to members whose every drop gets refused.
  canEditSomeCircle(
    memberId: string | undefined,
    isOrgMember: boolean,
    isOrgOwner: boolean
  ): boolean {
    if (!isOrgMember) return false
    if (this.governanceMode === Governance_Mode_Enum.Free) return true
    return this.circles.some((circle) => {
      const role = this.getRole(circle.roleId)
      if (!role) return false
      const perms = this.getCirclePermissions(
        circle,
        role,
        memberId,
        isOrgMember,
        isOrgOwner
      )
      return (
        perms.canEditCircle ||
        perms.canEditMembers ||
        perms.canEditSubCircles ||
        perms.canEditSubCirclesParentLinks
      )
    })
  }

  // Member ids included in a participants scope
  getScopeMemberIds(scope: ParticipantsScope): string[] {
    const memberIds = new Set(scope.members)

    for (const circle of scope.circles) {
      const participants = this.getParticipants(circle.id, !!circle.children)
      for (const participant of participants) {
        const memberId = participant.member.id
        if (circle.excludeMembers.includes(memberId)) continue
        memberIds.add(memberId)
      }
    }

    return [...memberIds]
  }

  // ---- Filters by member ----

  filterScopedEntities<Entity extends EntityWithScope>(
    data: Entity[],
    memberId?: string
  ): Entity[] {
    if (!memberId) return data

    const memberCircles = this.getParticipantCircles(memberId)
    const memberCirclesIds = new Set(memberCircles.map((c) => c.id))
    const memberParentCircleIds = new Set(
      memberCircles.flatMap((circle) => this.parentsOf(circle).map((c) => c.id))
    )

    return data.filter(
      ({ scope }) =>
        scope.members.includes(memberId) ||
        scope.circles.some(
          ({ id, children, excludeMembers }) =>
            !excludeMembers.includes(memberId) &&
            (memberCirclesIds.has(id) ||
              (children && memberParentCircleIds.has(id)))
        )
    )
  }

  filterThreads<Entity extends ThreadFragment>(
    threads: Entity[],
    memberId?: string
  ): Entity[] {
    if (!memberId) return threads

    const memberCirclesIds = new Set(
      this.getParticipantCircles(memberId).map((c) => c.id)
    )

    return threads.filter(
      ({ circleId, extra_members }) =>
        extra_members.some((em) => em.memberId === memberId) ||
        memberCirclesIds.has(circleId)
    )
  }
}
